import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core-radio-group';
import '@maersk-global/mds-components-core-radio';
import { ToastrService } from '../../services/toastr-service';
import { RepairModeService } from '../../services/repair-mode.service';
import { RepairModeListItem } from '../../models/repair-mode-list-item.model';
import { ErrorComponent } from '../../shared-components/error/error.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-create-repair-mode',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ErrorComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './create-repair-mode.component.html',
  styleUrls: ['./create-repair-mode.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreateRepairModeComponent implements OnInit, OnDestroy {
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  showCancelDialog = signal<boolean>(false);
  openAuditLog = false;
  repairModeCode: string | null = null;
  repairModeData = signal<RepairModeListItem | null>(null);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  createRepairModeForm = this.formBuilder.group({});

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly repairModeService: RepairModeService,
  ) {}

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    this.repairModeCode = this.route.snapshot.paramMap.get('mode');
    this.addControlsToTheFormGroup();
    if (this.repairModeCode) {
      this.isEditMode.set(true);
      await this.loadRepairModeData(this.repairModeCode);
    } else {
      this.isEditMode.set(false);
      this.createRepairModeForm.get('mode')?.enable({ emitEvent: false });
    }
  }

  private async loadRepairModeData(mode: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.repairModeService.getRepairModeByMode(mode));
      if (!data) {
        throw new Error('No data returned');
      }
      this.repairModeData.set(data);
      this.populateFormWithData(data);
      this.createRepairModeForm.get('mode')?.disable({ emitEvent: false });
    } catch {
      this.toastrService.showToastr('Failed to load repair mode data', 'error');
      this.router.navigate(['/master-data/repair-mode']);
    }
  }

  private populateFormWithData(data: RepairModeListItem): void {
    this.createRepairModeForm.patchValue({
      mode: data.mode,
      description: data.description,
      isActive: String(data.isActive),
    });
    this.createRepairModeForm.markAsPristine();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private addControlsToTheFormGroup(): void {
    this.createRepairModeForm.addControl(
      'mode',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(10)]),
    );
    this.createRepairModeForm.addControl(
      'description',
      this.formBuilder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(200)]),
    );
    this.createRepairModeForm.addControl(
      'isActive',
      this.formBuilder.nonNullable.control<string>('', [Validators.required]),
    );
  }

  isControlInErrorState(control: FormControl | AbstractControl | null | undefined): boolean | null {
    return control?.invalid && control?.touched ? true : null;
  }

  onCancel(): void {
    if (this.createRepairModeForm.dirty) {
      this.showCancelDialog.set(true);
    } else {
      this.navigateToRepairMode();
    }
  }

  confirmCancel(): void {
    this.showCancelDialog.set(false);
    this.navigateToRepairMode();
  }

  keepEditing(): void {
    this.showCancelDialog.set(false);
  }

  private navigateToRepairMode(): void {
    this.router.navigate(['/master-data/repair-mode']);
  }

  onSubmit(): void {
    if (this.createRepairModeForm.invalid) {
      for (const key of Object.keys(this.createRepairModeForm.controls)) {
        this.createRepairModeForm.get(key)?.markAsTouched();
      }
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }
    if (this.isEditMode()) {
      this.updateRepairMode();
    } else {
      this.createRepairMode();
    }
  }

  private createRepairMode(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const formValue = this.createRepairModeForm.getRawValue() as { mode: string; description: string; isActive: string };

    this.repairModeService
      .createRepairMode({ mode: formValue.mode, description: formValue.description, isActive: formValue.isActive === 'true' })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: RepairModeListItem) => {
          this.submitLoader.set(false);
          this.createRepairModeForm.reset();
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Repair Mode '${response.mode}' created successfully`, 'success');
          this.router.navigate(['/master-data/repair-mode']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleRepairModeError(error, 'creating');
        },
      });
  }

  private updateRepairMode(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);
    const formValue = this.createRepairModeForm.getRawValue() as { mode: string; description: string; isActive: string };

    this.repairModeService
      .updateRepairMode(this.repairModeCode!, { description: formValue.description, isActive: formValue.isActive === 'true' })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: RepairModeListItem) => {
          this.submitLoader.set(false);
          this.formSubmitErrors.set([]);
          this.toastrService.showToastr(`Repair Mode '${response.mode}' updated successfully`, 'success');
          this.router.navigate(['/master-data/repair-mode']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleRepairModeError(error, 'updating');
        },
      });
  }

  private handleRepairModeError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);
    if (error.status === 400) {
      this.formSubmitErrors.set(['Invalid data. Please check your inputs.']);
      this.toastrService.showToastr('Invalid data. Please check your inputs.', 'error');
    } else if (error.status === 409) {
      this.formSubmitErrors.set(['This repair mode already exists.']);
      this.toastrService.showToastr('This repair mode already exists.', 'error');
    } else if (error.status === 422 && error.error?.violations) {
      const errors = (error.error.violations as { message: string }[]).map((v) => v.message);
      this.formSubmitErrors.set(errors);
      this.toastrService.showToastr('Validation errors occurred. Please check the form.', 'error');
    } else {
      this.formSubmitErrors.set([`Failed to ${operation} repair mode. Please try again.`]);
      this.toastrService.showToastr(`Failed to ${operation} repair mode. Please try again.`, 'error');
    }
  }

  onAuditLogClick(): void {
    if (!this.repairModeCode) {
      this.toastrService.showToastr('Repair mode not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.repairModeService.getAuditLogs(this.repairModeCode!, isSystemLog, page, limit);
}
